import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { ReactComponent as InnovationIllustration } from 'presentation/assets/illustrations/innovation.svg'
import { ReactComponent as PlusIcon } from 'presentation/assets/icons/plus.svg'
import { ProcedureRecommendationCard } from 'presentation/doctor/components/ProcedureRecommendationCard'
import { useServices } from 'presentation/hooks/use-services'
import { useStores } from 'presentation/hooks/use-stores'
import { useMedia } from 'presentation/utils/window-media'
import ProvidersTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/ProvidersTable'
import ProceduresTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/ProceduresTable'
import {
  ProcedureV2Props,
  ProviderV2Props,
  OpmeV2Props,
  CidV2Props,
  OrderStepsPath
} from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import { SurgicalOrderRecommendation } from 'domain/usecases/surgical-order-recommendation/get-procedures-recommendations'
import { ProcedureModel } from 'domain/entities/surgical-order-model'
import normalizeText from 'common/utils/getNormalizedText'
import OpmesTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/OpmesTable'
import CidsTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/CidsTable'
import ProceduresList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/ProceduresList'
import ProvidersList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/ProvidersList'
import CidsList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/CidsList'
import OpmesList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/OpmesList'
import { observer } from 'mobx-react'
import AddProcedureResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/AddProcedureResponsiveModal'
import AddCidResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/AddCidResponsiveModal'
import AddProviderResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/AddProviderResponsiveModal'
import AddOpmeResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/AddOpmeResponsiveModal'
import capitalizeFirstLetter from 'common/utils/capitalizeFirstLetter'
import { filterByDataSource } from 'presentation/contexts/surgical-order-v2/utils/filterByDataSource'
import { v4 as uuidv4 } from 'uuid'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'

type ProcedureSuggestion = {
  label: string
  value: ProcedureModel
}

const ProceduresAndOpme = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const store = useStores().surgicalOrderV2
    const currentUser = useStores().currentAccount.getCurrentAccount().user

    const [isLoadingRecommendations, setIsLoadingRecommendations] =
      useState<boolean>(false)
    const surgicalOrderService = useServices().surgicalOrder
    const doctorService = useServices().doctor
    const surgicalOrderRecommendationService =
      useServices().surgicalOrderRecommendation
    const media = useMedia()
    const [procedureSearchTerm, setProcedureSearchTerm] = useState('')
    const [proceduresList, setProceduresList] = useState<ProcedureSuggestion[]>(
      []
    )
    const [selectedProcedure, setSelectedProcedure] = useState<any>(null)
    const [recommendations, setRecommendations] = useState<
      SurgicalOrderRecommendation[]
    >([])
    const [procedures, setProcedures] = useState<ProcedureV2Props[]>(
      store.procedures
    )
    const [opmes, setOpmes] = useState<OpmeV2Props[]>(
      store.opmes.length > 0 ? store.opmes : []
    )
    const [opmeJustification, setOpmeJustification] = useState(
      store.opmeJustification
    )
    const [providers, setProviders] = useState<ProviderV2Props[]>(
      store.providers.length > 0 ? store.providers : []
    )
    const [cids, setCids] = useState<CidV2Props[]>(
      store.cids.length > 0 ? store.cids : []
    )
    const [isAddProcedureModalOpen, setIsAddProcedureModalOpen] =
      useState(false)
    const [isAddCidModalOpen, setIsAddCidModalOpen] = useState(false)
    const [isAddProviderModalOpen, setIsAddProviderModalOpen] = useState(false)
    const [isAddOpmeModalOpen, setIsAddOpmeModalOpen] = useState(false)

    useEffect(() => {
      const loadRecommendations = async () => {
        try {
          setIsLoadingRecommendations(true)

          const doctor = await doctorService.loadDoctor(['doctor_id'])
          const healthInsuranceCode = store.healthInsurance.healthInsuranceCode

          const response =
            await surgicalOrderRecommendationService.getProceduresRecommendations(
              {
                doctorId: doctor.doctor_id,
                healthInsuranceCode: healthInsuranceCode
                  ? Number(healthInsuranceCode)
                  : undefined
              }
            )

          setRecommendations(response.recommendations)
        } catch (error) {
          toast.error('Erro ao carregar recomendações')
        } finally {
          setIsLoadingRecommendations(false)
        }
      }

      loadRecommendations()
    }, [])

    useEffect(() => {
      if (store.hasProcedures && store.procedures.length > 0) {
        const mainProcedure = store.procedures.find((p) => p.is_main)
        if (mainProcedure) {
          setSelectedProcedure(mainProcedure)
          setProcedureSearchTerm(
            `${mainProcedure.code} - ${mainProcedure.description}`
          )
        }
      }
    }, [])

    useEffect(() => {
      const hasSelectedLowAcceptanceOpme = opmes.some(
        (opme) => opme.low_acceptance && opme.is_selected
      )
      const isJustificationMissing =
        hasSelectedLowAcceptanceOpme && !opmeJustification.trim()

      const isValid =
        store.hasProcedures &&
        store.hasMainProcedure &&
        store.hasCids &&
        !isJustificationMissing

      store.setIsStepValid(isValid)
    }, [opmes, opmeJustification, store.procedures, store.cids])

    const buildPayload = () => ({
      surgical_order_id: store.surgicalOrder?.surgicalOrderId,
      patient: {
        patient_id: store.patient?.patientId,
        patient_name: store.patient?.name
      },
      procedure: procedures.map((p) => ({
        ...p,
        isMain: p.is_main,
        procedure_code: p.code
      })),
      cid: cids,
      opme: {
        denied_reason: opmeJustification?.trim(),
        solicitations: opmes,
        providers: providers.map((p) => p.name),
        providers_new: providers
      }
    })

    const saveProceduresAndOpme = async (onSuccess?: () => void) => {
      try {
        setIsLoading(true)
        await store.updateSurgicalOrder(surgicalOrderService, buildPayload())
        store.markStepCompleted(store.currentStepIndex)
        onSuccess?.()
      } catch (error) {
        toast.error('Ocorreu um erro ao atualizar os procedimentos e OPMES')
      } finally {
        setIsLoading(false)
      }
    }

    const validateForm = (): boolean => {
      if (procedures.length < 1) {
        toast.error('É necessário adicionar pelo menos 1 procedimento')
        return false
      }

      if (!procedures.some((procedure) => procedure.is_main)) {
        toast.error('É necessário adicionar 1 procedimento como principal')
        return false
      }

      if (cids.length < 1) {
        toast.error('É necessário adicionar pelo menos 1 CID')
        return false
      }

      const hasSelectedLowAcceptanceOpme = opmes.some(
        (opme) => opme.low_acceptance && opme.is_selected
      )

      if (hasSelectedLowAcceptanceOpme && !opmeJustification.trim()) {
        toast.error(
          'É necessário preencher a justificativa para OPMEs com histórico de negativa'
        )
        return false
      }

      if (
        filterByDataSource(opmes).hasVisible &&
        !filterByDataSource(providers).hasVisible
      ) {
        toast.error('É necessário indicar pelo menos 1 fornecedor')
        return false
      }

      return true
    }

    useEffect(() => {
      if (store.submitFormTriggered) {
        if (validateForm()) {
          saveProceduresAndOpme(() =>
            history.replace(OrderStepsPath.CLINICAL_HISTORY)
          )
        }
        store.resetSubmitTrigger()
      }
    }, [store.submitFormTriggered])

    useEffect(() => {
      if (store.saveOnlyTriggered) {
        saveProceduresAndOpme(() => toast.success('As alterações foram salvas'))
        store.resetSaveOnlyTrigger()
      }
    }, [store.saveOnlyTriggered])

    const getProcedures = async (searchTerm: string) => {
      try {
        if (searchTerm) {
          let searchParams

          if (isNaN(Number(searchTerm))) {
            searchParams = { name: searchTerm }
          } else {
            searchParams = { code: searchTerm }
          }

          const searchProcedures = await surgicalOrderService.searchProcedures(
            searchParams
          )

          const suggestionsProcedures = searchProcedures.map((procedure) => ({
            label: `${procedure.tuss_id} - ${procedure.description}`,
            value: procedure
          }))

          setProceduresList(suggestionsProcedures)
        } else {
          setProceduresList([])
        }
      } catch (error: any) {
        toast.error('Ocorreu um erro ao buscar procedimentos')
        setProceduresList([])
      }
    }

    const selectProcedure = (procedure: any) => {
      clearSelection()

      const mainProcedure: ProcedureV2Props = {
        code: procedure.value.tuss_id,
        description: procedure.value.description,
        quantity: 1,
        is_main: true,
        source: DataSource.TEMPLATE,
        is_selected: true,
        surgery_id: procedure.value.surgery_id,
        pro_fat_id: procedure.value.pro_fat_id,
        doctor_name: currentUser.name
      }
      setProcedures([mainProcedure])
      setProcedureSearchTerm(procedure.label)
      setSelectedProcedure(procedure.value)
    }

    const clearSelection = () => {
      setProcedureSearchTerm('')
      setSelectedProcedure(null)
      setProcedures([])
      setOpmes([])
      setOpmeJustification('')
      setProviders([])
      setCids([])
    }

    const handleSelectRecommendedProcedure = (
      recommendation: SurgicalOrderRecommendation
    ) => {
      const mainProcedure: ProcedureV2Props = {
        code: recommendation.code,
        description: recommendation.description,
        quantity: 1,
        is_main: true,
        source: DataSource.TEMPLATE,
        is_selected: true,
        surgery_id: recommendation.surgery_id,
        pro_fat_id: recommendation.pro_fat_id,
        doctor_name: currentUser.name
      }

      const additionalProcedures: ProcedureV2Props[] = recommendation.procedures
        .map((procedure) => ({
          code: procedure.code,
          description: procedure.description,
          quantity: procedure.frequentQuantity,
          is_main: false,
          source: DataSource.TEMPLATE,
          is_selected: true,
          surgery_id: recommendation.surgery_id,
          pro_fat_id: procedure.pro_fat_id,
          doctor_name: currentUser.name
        }))
        .sort((a, b) => a.description.localeCompare(b.description, 'pt-BR'))

      const newProcedures: ProcedureV2Props[] = [
        mainProcedure,
        ...additionalProcedures
      ]

      if (recommendation.opme.length > 0) {
        const initialOpmes: OpmeV2Props[] = recommendation.opme.map((opme) => {
          const lowAcceptance =
            opme.totalAuthorized < opme.totalRequested * 0.5 &&
            opme.totalRequested > 0

          return {
            code: uuidv4(),
            description: opme.description,
            quantity: opme.frequentQuantity || 1,
            low_acceptance: lowAcceptance,
            source: DataSource.TEMPLATE,
            is_selected: !lowAcceptance
          }
        })

        const sortedOpmes = initialOpmes.sort((a, b) => {
          if (a.low_acceptance !== b.low_acceptance) {
            return a.low_acceptance ? 1 : -1
          }
          return a.description.localeCompare(b.description, 'pt-BR')
        })

        setOpmes(sortedOpmes)
      }

      if (providers.length === 0 && recommendation.providers.length > 0) {
        const initialProviders: ProviderV2Props[] = recommendation.providers
          .map((provider) => ({
            code: uuidv4(),
            name: provider.name,
            source: DataSource.TEMPLATE,
            is_selected: true
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        setProviders(initialProviders)
      }

      setProcedures(newProcedures)
      setProcedureSearchTerm(
        `${recommendation.code} - ${recommendation.description}`
      )
      setSelectedProcedure(recommendation)
    }

    const handleProcedureDelete = (procedureCode: string) => {
      setProcedures((currentProcedures) =>
        currentProcedures.filter((p) => p.code !== procedureCode)
      )

      if (procedures.length <= 1) {
        clearSelection()
      }
    }

    const handleProcedureQuantityChange = (
      procedureCode: string,
      quantity: number
    ) => {
      setProcedures((currentProcedures) =>
        currentProcedures.map((p) =>
          p.code === procedureCode ? { ...p, quantity } : p
        )
      )
    }

    const handleProcedureToggleSelection = (procedureCode: string) => {
      setProcedures((currentProcedures) =>
        currentProcedures.map((p) =>
          p.code === procedureCode && !p.is_main
            ? { ...p, is_selected: !p.is_selected }
            : p
        )
      )
    }

    const handleOpmeDelete = (opmeCode: string) => {
      setOpmes((currentOpmes) =>
        currentOpmes.filter((o) => o.code !== opmeCode)
      )
    }

    const handleOpmeQuantityChange = (opmeCode: string, quantity: number) => {
      setOpmes((currentOpmes) =>
        currentOpmes.map((o) => (o.code === opmeCode ? { ...o, quantity } : o))
      )
    }

    const handleOpmeToggleSelection = (opmeCode: string) => {
      const updatedOpmes = opmes.map((o) =>
        o.code === opmeCode ? { ...o, is_selected: !o.is_selected } : o
      )

      setOpmes(updatedOpmes)

      const hasSelectedLowAcceptanceOpme = updatedOpmes.some(
        (opme) => opme.low_acceptance && opme.is_selected
      )

      if (!hasSelectedLowAcceptanceOpme) setOpmeJustification('')
    }

    const handleProviderDelete = (providerCode: string) => {
      setProviders((currentProviders) =>
        currentProviders.filter((p) => p.code !== providerCode)
      )
    }

    const handleProviderToggleSelection = (providerCode: string) => {
      setProviders((currentProviders) =>
        currentProviders.map((p) =>
          p.code === providerCode ? { ...p, is_selected: !p.is_selected } : p
        )
      )
    }

    const handleCidDelete = (cidCode: string) => {
      setCids((currentCids) =>
        currentCids
          .filter((c) => c.code !== cidCode)
          .map((c, index) => {
            return { ...c, is_main: index === 0 }
          })
      )
    }

    const handleAddProcedure = () => {
      setIsAddProcedureModalOpen(true)
    }

    const handleAddOpme = () => {
      setIsAddOpmeModalOpen(true)
    }

    const handleAddProvider = () => {
      setIsAddProviderModalOpen(true)
    }

    const handleAddCid = () => {
      setIsAddCidModalOpen(true)
    }

    return (
      <>
        <HeadingNew
          as="h1"
          color="textPrimary"
          size="xlarge"
          style={{ lineHeight: 1 }}
        >
          Procedimentos e OPME
        </HeadingNew>
        <SupportTextNew
          color="textSecondary"
          fontFamily="secondary"
          size="medium"
          style={{ marginBottom: '24px' }}
        >
          Adicione o procedimento principal para criar o seu pedido cirúrgico
        </SupportTextNew>

        {!selectedProcedure && isLoadingRecommendations ? (
          <S.LoadingWrapper>
            <InnovationIllustration
              width={media.sm ? 340 : 280}
              height={media.sm ? 340 : 280}
            />
            <SupportTextNew
              color="textDisabled"
              size={media.sm ? 'small' : 'xxsmall'}
              weight="bold"
              style={{ textAlign: 'center', maxWidth: '400px' }}
            >
              Aguarde uns instantes... estamos preparando tudo para agilizar a
              criação do seu pedido cirúrgico
            </SupportTextNew>
          </S.LoadingWrapper>
        ) : (
          <S.Wrapper>
            <S.AutoCompleteWrapper>
              <AutoCompleteNew
                name="procedure"
                placeholder="Digite código TUSS ou nome"
                onInputChange={setProcedureSearchTerm}
                returnFullSuggestion
                label="Busque procedimento principal pelo código TUSS ou nome"
                labelSize="xsmall"
                labelWeight="normal"
                labelColor="textPrimary"
                iconPosition="insideLeft"
                icon={<SearchIcon />}
                minCharactersSearch={1}
                suggestions={proceduresList || []}
                onSuggestionClick={(procedure) => selectProcedure(procedure)}
                value={procedureSearchTerm}
                onType={getProcedures}
                debounceDelay={800}
                disabled={!!selectedProcedure}
                clear={clearSelection}
                height="40px"
                useClearableInput
              />
            </S.AutoCompleteWrapper>

            {!selectedProcedure && (
              <S.RecommendationsSection>
                <HeadingNew
                  as="h2"
                  color="textPrimary"
                  weight="normal"
                  size="xsmall"
                >
                  Ou selecione um dos procedimentos mais realizados por você
                </HeadingNew>
                <S.ProceduresGrid>
                  {!!procedureSearchTerm && procedureSearchTerm.length >= 1
                    ? recommendations
                        .filter((r) => {
                          const normalizedSearchTerm = normalizeText(
                            procedureSearchTerm.toLowerCase()
                          )
                          const normalizedDescription = normalizeText(
                            r.description.toLowerCase()
                          )
                          const normalizedCode = r.code.toString()

                          return (
                            normalizedDescription.includes(
                              normalizedSearchTerm
                            ) || normalizedCode.includes(normalizedSearchTerm)
                          )
                        })
                        .map((recommendation) => (
                          <ProcedureRecommendationCard
                            key={recommendation.code}
                            code={Number(recommendation.code)}
                            description={capitalizeFirstLetter(
                              recommendation.description
                            )}
                            isFiltered
                            onSelect={() =>
                              handleSelectRecommendedProcedure(recommendation)
                            }
                          />
                        ))
                    : recommendations.map((recommendation) => (
                        <ProcedureRecommendationCard
                          key={recommendation.code}
                          code={Number(recommendation.code)}
                          description={capitalizeFirstLetter(
                            recommendation.description
                          )}
                          isFiltered={false}
                          onSelect={() =>
                            handleSelectRecommendedProcedure(recommendation)
                          }
                        />
                      ))}
                </S.ProceduresGrid>
              </S.RecommendationsSection>
            )}

            {selectedProcedure && (
              <>
                <S.RecommendationsSection>
                  <HeadingNew
                    as="h2"
                    color="textSecondary"
                    weight="bold"
                    size="xsmall"
                  >
                    Procedimentos adicionados:
                  </HeadingNew>

                  {media.md ? (
                    <>
                      <ProceduresTable
                        initialRows={procedures}
                        onDelete={handleProcedureDelete}
                        onQuantityChange={handleProcedureQuantityChange}
                        onToggleSelection={handleProcedureToggleSelection}
                        formatDescription
                      />

                      <S.AddButton>
                        <ButtonNew
                          outlined
                          onClick={handleAddProcedure}
                          variant="text"
                          icon={<PlusIcon />}
                          fontSize="xsmall"
                          noPadding
                        >
                          Adicionar outro procedimento
                        </ButtonNew>
                      </S.AddButton>
                    </>
                  ) : (
                    <ProceduresList
                      initialRows={procedures}
                      onDelete={handleProcedureDelete}
                      onQuantityChange={handleProcedureQuantityChange}
                      onToggleSelection={handleProcedureToggleSelection}
                      onAddProcedure={handleAddProcedure}
                      showAddButton
                      formatDescription
                    />
                  )}
                </S.RecommendationsSection>

                <S.RecommendationsSection>
                  <HeadingNew
                    as="h2"
                    color="textSecondary"
                    weight="bold"
                    size="xsmall"
                  >
                    CIDs adicionados:
                  </HeadingNew>

                  {media.md ? (
                    <>
                      <CidsTable
                        initialRows={cids}
                        onDelete={handleCidDelete}
                      />

                      <S.AddButton>
                        <ButtonNew
                          outlined
                          onClick={handleAddCid}
                          variant="text"
                          icon={<PlusIcon />}
                          fontSize="xsmall"
                          noPadding
                        >
                          Adicionar CID
                        </ButtonNew>
                      </S.AddButton>
                    </>
                  ) : (
                    <CidsList
                      initialRows={cids}
                      onDelete={handleCidDelete}
                      onAddCid={handleAddCid}
                      showAddButton
                    />
                  )}
                </S.RecommendationsSection>

                <S.RecommendationsSection>
                  <HeadingNew
                    as="h2"
                    color="textSecondary"
                    weight="bold"
                    size="xsmall"
                  >
                    OPMEs adicionados:
                  </HeadingNew>

                  {media.md ? (
                    <>
                      <OpmesTable
                        initialRows={opmes}
                        onDelete={handleOpmeDelete}
                        onQuantityChange={handleOpmeQuantityChange}
                        onToggleSelection={handleOpmeToggleSelection}
                        justification={opmeJustification}
                        onJustificationChange={setOpmeJustification}
                      />

                      <S.AddButton>
                        <ButtonNew
                          outlined
                          onClick={handleAddOpme}
                          variant="text"
                          icon={<PlusIcon />}
                          fontSize="xsmall"
                          noPadding
                        >
                          Adicionar outro OPME
                        </ButtonNew>
                      </S.AddButton>
                    </>
                  ) : (
                    <OpmesList
                      initialRows={opmes}
                      onDelete={handleOpmeDelete}
                      onQuantityChange={handleOpmeQuantityChange}
                      onToggleSelection={handleOpmeToggleSelection}
                      justification={opmeJustification}
                      onJustificationChange={setOpmeJustification}
                      onAddOpme={handleAddOpme}
                      showAddButton
                    />
                  )}
                </S.RecommendationsSection>

                <S.RecommendationsSection>
                  <HeadingNew
                    as="h2"
                    color="textSecondary"
                    weight="bold"
                    size="xsmall"
                  >
                    Fornecedores indicados:
                  </HeadingNew>

                  {media.md ? (
                    <>
                      <ProvidersTable
                        initialRows={providers}
                        onDelete={handleProviderDelete}
                        onToggleSelection={handleProviderToggleSelection}
                      />

                      <S.AddButton>
                        <ButtonNew
                          outlined
                          onClick={handleAddProvider}
                          variant="text"
                          icon={<PlusIcon />}
                          fontSize="xsmall"
                          noPadding
                        >
                          Indicar outro fornecedor
                        </ButtonNew>
                      </S.AddButton>
                    </>
                  ) : (
                    <ProvidersList
                      initialRows={providers}
                      onDelete={handleProviderDelete}
                      onAddProvider={handleAddProvider}
                      onToggleSelection={handleProviderToggleSelection}
                      showAddButton
                    />
                  )}
                </S.RecommendationsSection>
              </>
            )}
          </S.Wrapper>
        )}

        <AddProcedureResponsiveModal
          isOpen={isAddProcedureModalOpen}
          onClose={() => setIsAddProcedureModalOpen(false)}
          onConfirm={(updatedProcedures) => {
            setProcedures((prev) => [
              ...prev.filter((p) => p.source !== DataSource.DOCTOR),
              ...updatedProcedures
            ])
            setIsAddProcedureModalOpen(false)
          }}
          procedures={procedures.filter((p) => p.source === DataSource.DOCTOR)}
          allProcedures={procedures}
        />

        <AddCidResponsiveModal
          isOpen={isAddCidModalOpen}
          onClose={() => setIsAddCidModalOpen(false)}
          onConfirm={(updatedCids) => {
            setCids(updatedCids)
            setIsAddCidModalOpen(false)
          }}
          cids={cids}
        />

        <AddProviderResponsiveModal
          isOpen={isAddProviderModalOpen}
          onClose={() => setIsAddProviderModalOpen(false)}
          onConfirm={(updatedProviders) => {
            setProviders((prev) => [
              ...prev.filter((p) => p.source !== DataSource.DOCTOR),
              ...updatedProviders
            ])
            setIsAddProviderModalOpen(false)
          }}
          providers={providers.filter((p) => p.source === DataSource.DOCTOR)}
          allProviders={providers}
        />

        <AddOpmeResponsiveModal
          isOpen={isAddOpmeModalOpen}
          onClose={() => setIsAddOpmeModalOpen(false)}
          onConfirm={(updatedOpmes) => {
            setOpmes((prev) => [
              ...prev.filter((o) => o.source !== DataSource.DOCTOR),
              ...updatedOpmes
            ])
            setIsAddOpmeModalOpen(false)
          }}
          opmes={opmes.filter((o) => o.source === DataSource.DOCTOR)}
          allOpmes={opmes}
        />
      </>
    )
  })
)

export default ProceduresAndOpme
